import { gql, useMutation } from '@apollo/client';

export const aiProfileFields = ['BIO', 'ACCOMPLISHMENTS'] as const;
export type AiProfileField = (typeof aiProfileFields)[number];

export const aiTones = [
  'WARM_AND_APPROACHABLE',
  'BOLD_AND_MOTIVATING',
  'EXPERT_AND_CREDIBLE',
  'CASUAL_AND_FUN',
] as const;
export type AiTone = (typeof aiTones)[number];

export const aiStyles = ['SHORT', 'MEDIUM', 'LONG'] as const;
export type AiStyle = (typeof aiStyles)[number];

export const aiToneLabels: Record<AiTone, string> = {
  WARM_AND_APPROACHABLE: 'Warm & Approachable',
  BOLD_AND_MOTIVATING: 'Bold & Motivating',
  EXPERT_AND_CREDIBLE: 'Expert & Credible',
  CASUAL_AND_FUN: 'Casual & Fun',
};

export const aiStyleLabels: Record<AiStyle, string> = {
  SHORT: 'Short',
  MEDIUM: 'Medium',
  LONG: 'Long',
};

type GenerateProfileTextInput = {
  field: AiProfileField;
  tone: AiTone;
  style: AiStyle;
  thingsToMention?: string;
  initialInput?: string;
};

export type GeneratedOption = {
  text: string;
  wordCount: number;
};

type GenerateProfileTextResult = {
  options: GeneratedOption[];
  remainingGenerations: number;
};

const GENERATE_PROFILE_TEXT = gql`
  mutation GenerateProfileText(
    $creatorId: Int!
    $input: GenerateProfileTextInput!
  ) {
    generateProfileText(creatorId: $creatorId, input: $input) {
      options {
        text
        wordCount
      }
      remainingGenerations
    }
  }
`;

export const useGenerateProfileTextModel = (creatorId: number) => {
  const [mutate] = useMutation<
    { generateProfileText: GenerateProfileTextResult },
    { creatorId: number; input: GenerateProfileTextInput }
  >(GENERATE_PROFILE_TEXT);

  const generate = async (
    input: GenerateProfileTextInput,
  ): Promise<GenerateProfileTextResult> => {
    const { data } = await mutate({
      variables: { creatorId, input },
    });

    if (!data) {
      throw new Error('Failed to generate text');
    }

    return data.generateProfileText;
  };

  return { generate };
};
